import { useState, useMemo, useEffect, useCallback } from 'react';
import { Flex, Button, Text, Select } from '@chakra-ui/react';

const EMPTY = [];   // tham chiếu ổn định -> tránh useMemo tính lại mỗi render khi items rỗng/null

/**
 * Hook phân trang CLIENT-SIDE: cắt 1 mảng đã tải sẵn theo trang.
 * - `resetKey`: đổi (vd chuỗi tìm kiếm / lớp) -> tự về trang 1.
 * - Tự kẹp trang trong khoảng hợp lệ khi tổng số / cỡ trang thay đổi.
 * Trả về `pageItems` để render + các trường điều khiển cho <Pagination pager={...} />.
 */
export function usePaged(items, initialSize = 20, resetKey) {
  const list = items || EMPTY;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => { setPage(1); }, [resetKey, pageSize]);       // đổi filter / cỡ trang -> về trang 1
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const cur = Math.min(page, pageCount);
  const start = (cur - 1) * pageSize;
  const pageItems = useMemo(() => (items || EMPTY).slice(start, start + pageSize), [items, start, pageSize]);

  return {
    pageItems, page: cur, setPage, pageSize, setPageSize,
    total, pageCount, from: total ? start + 1 : 0, to: Math.min(start + pageSize, total),
  };
}

/** Cửa sổ số trang rút gọn: 1 … 4 5 [6] 7 8 … 20 */
function pageWindow(page, count) {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const out = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(count - 1, page + 1);
  if (lo > 2) out.push('…');
  for (let i = lo; i <= hi; i += 1) out.push(i);
  if (hi < count - 1) out.push('…');
  out.push(count);
  return out;
}

/**
 * Thanh điều hướng trang — nhận trực tiếp object từ usePaged qua prop `pager`.
 * Ẩn khi không có mục nào. `label` = tên đơn vị (câu / máy / tài khoản...).
 */
export default function Pagination({ pager, label = 'mục', sizes = [10, 20, 50, 100], showSize = true }) {
  const { page, pageCount, total, from, to, setPage, pageSize, setPageSize } = pager;
  if (total === 0) return null;
  const nums = pageWindow(page, pageCount);
  return (
    <Flex align="center" justify="space-between" mt="16px" gap="10px" wrap="wrap">
      <Text fontSize="sm" color="secondaryGray.600">
        {from}–{to} / {total.toLocaleString('vi-VN')} {label}
      </Text>
      <Flex align="center" gap="6px" wrap="wrap">
        <Button size="sm" variant="ghost" onClick={() => setPage(page - 1)} isDisabled={page <= 1}>‹</Button>
        {nums.map((n, i) => (n === '…'
          ? <Text key={`e${i}`} px="6px" color="secondaryGray.600">…</Text>
          : (
            <Button key={n} size="sm" minW="34px" px="8px"
              variant={n === page ? 'solid' : 'ghost'} colorScheme={n === page ? 'brand' : 'gray'}
              onClick={() => setPage(n)}>{n}</Button>
          )))}
        <Button size="sm" variant="ghost" onClick={() => setPage(page + 1)} isDisabled={page >= pageCount}>›</Button>
        {showSize && (
          <Select size="sm" w="auto" ml="6px" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {sizes.map((s) => <option key={s} value={s}>{s}/trang</option>)}
          </Select>
        )}
      </Flex>
    </Flex>
  );
}

/**
 * Hook phân trang SERVER-SIDE: backend trả { items, total, page, pageSize }.
 * Dùng cho danh sách có thể phình to (tài khoản, máy, câu hỏi, yêu cầu đổi thưởng) —
 * client-side sẽ phải kéo cả bảng về, vừa chậm vừa dễ làm cạn connection pool của API.
 *
 * `fetchPage({page,pageSize})` -> Promise<{items,total,page,pageSize}>.
 * `resetKey` CHỈ dùng để quay về trang 1 khi bộ lọc đổi — tham số lọc do caller tự ghép vào URL
 * (trước đây hook tự nhét resetKey vào `q`, nên màn nào có resetKey ghép nhiều cờ thì gửi lên
 * chuỗi rác và server không tìm thấy gì).
 * Trả về cùng hình dạng với usePaged nên <Pagination pager={...} /> dùng chung được.
 */
export function useServerPaged(fetchPage, { initialSize = 20, resetKey = '', deps = [] } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const [state, setState] = useState({ items: EMPTY, total: 0, loading: true, error: null });

  useEffect(() => { setPage(1); }, [resetKey, pageSize]);

  const load = useCallback(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchPage({ page, pageSize })
      .then((r) => alive && setState({
        items: r?.items || EMPTY, total: r?.total || 0, loading: false, error: null,
      }))
      .catch((e) => alive && setState({
        items: EMPTY, total: 0, loading: false, error: e.message || 'Lỗi tải dữ liệu',
      }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, resetKey, ...deps]);

  useEffect(load, [load]);

  const { items, total, loading, error } = state;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  return {
    pageItems: items, page, setPage, pageSize, setPageSize,
    total, pageCount, from: total ? start + 1 : 0, to: Math.min(start + items.length, total),
    loading, error, reload: load,
  };
}

/** Trễ giá trị (ô tìm kiếm) để không bắn 1 request mỗi ký tự gõ. */
export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
